/*
 * LIO-617: Sticky add-to-cart bar (snippets/pd-sticky-atc.liquid).
 *
 * The bar is a mirror of the main product form, never a second source of
 * truth:
 *  - its size picker selects through the main size selector (fake-selector),
 *    so variant re-rendering, back-in-stock and pre-order logic all stay in
 *    one place;
 *  - its button submits the main product form, so the cart drawer, line item
 *    properties and the size-required error behave exactly as on the main CTA;
 *  - size label, price and CTA state are copied from the main form whenever
 *    it changes, so a change on either side shows on both.
 */
if (!customElements.get('pd-sticky-atc')) {
  customElements.define(
    'pd-sticky-atc',
    class PdStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.toggleButton = this.querySelector('[data-sticky-toggle]');
        this.panel = this.querySelector('[data-sticky-panel]');
        this.list = this.querySelector('[data-sticky-list]');
        this.label = this.querySelector('[data-sticky-label]');
        this.select = this.querySelector('[data-sticky-select]');
        this.price = this.querySelector('[data-sticky-price]');
        this.submitButton = this.querySelector('[data-sticky-submit]');
        this.submitText = this.querySelector('[data-sticky-submit-text]');
        this.sizeGuideButton = this.querySelector('[data-sticky-size-guide]');

        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.scheduleSync = this.scheduleSync.bind(this);

        this.toggleButton?.addEventListener('click', () => this.togglePanel());
        this.list?.addEventListener('click', (event) => this.onOptionClick(event));
        this.submitButton?.addEventListener('click', () => this.onSubmit());
        this.sizeGuideButton?.addEventListener('click', () => this.openSizeGuide());
        this.addEventListener('keydown', this.onKeydown);

        this.observeMainForm();
        this.observeVisibility();
        this.sync();
      }

      disconnectedCallback() {
        this.mutationObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.onScroll = null;
        document.removeEventListener('click', this.onDocumentClick);
        document.body.classList.remove('pd-sticky-atc-visible');
      }

      /* ---------- main form lookups (re-queried: product-info swaps nodes) ---------- */

      get mainInfo() {
        return document.getElementById(`ProductInfo-${this.sectionId}`);
      }

      get mainSubmit() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get mainDropdown() {
        return this.mainInfo?.querySelector('.product-variant-selector__item-dropdown');
      }

      get mainOptions() {
        return Array.from(this.mainDropdown?.querySelectorAll('.collapse-variant') || []);
      }

      /* ---------- visibility ---------- */

      observeVisibility() {
        if (this.onScroll) return;

        // A passive scroll/resize check (one getBoundingClientRect per event)
        // rather than an IntersectionObserver: product-info can swap the
        // buy-buttons node on a variant change, which leaves an observer
        // watching a detached element.
        let ticking = false;
        this.onScroll = () => {
          if (ticking) return;
          ticking = true;
          setTimeout(() => {
            ticking = false;
            this.updateVisibility();
          }, 50);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.updateVisibility();
      }

      updateVisibility() {
        const target = this.mainSubmit?.closest('.product-form__buttons') || this.mainSubmit;
        if (!target) return this.setVisible(false);
        // Only once the main CTA has scrolled up past the top of the viewport.
        this.setVisible(target.getBoundingClientRect().bottom < 0);
      }

      setVisible(visible) {
        if (this.visible === visible) return;
        this.visible = visible;

        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('inert', !visible);
        this.setAttribute('aria-hidden', String(!visible));
        document.body.classList.toggle('pd-sticky-atc-visible', visible);

        if (!visible) this.closePanel();
      }

      /* ---------- mirror the main form ---------- */

      observeMainForm() {
        const root = this.mainInfo;
        if (!root) return;

        this.mutationObserver = new MutationObserver(this.scheduleSync);
        this.mutationObserver.observe(root, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled', 'data-cta-state', 'preorder'],
        });
      }

      // MutationObserver already batches records per microtask, so one sync
      // per batch is enough (no rAF: it stalls while the tab is hidden).
      scheduleSync() {
        this.sync();
      }

      sync() {
        this.syncSize();
        this.syncPrice();
        this.syncSubmit();
        if (this.panel && !this.panel.hidden) this.renderOptions();
        if (this.onScroll) this.updateVisibility();
      }

      syncSize() {
        if (!this.label) return;

        const dropdown = this.mainDropdown;
        const mainLabel = dropdown?.querySelector('.product-variant-selector__item-selector__header-label');
        if (!mainLabel) return;

        const text = mainLabel.textContent.trim();
        if (this.label.textContent !== text) this.label.textContent = text;

        // QC #84: an out-of-stock selected size is greyed in the main
        // dropdown (label[disabled]); mirror that here
        this.label.classList.toggle('is-unavailable', mainLabel.hasAttribute('disabled'));

        const selected = dropdown.classList.contains('is-selected');
        this.select.classList.toggle('is-selected', selected);
        if (selected) this.select.classList.remove('has-error');
      }

      syncPrice() {
        const mainPrice = document.querySelector(`#price-${this.sectionId} .price`);
        if (!mainPrice || !this.price) return;

        const html = mainPrice.outerHTML;
        if (this.lastPriceHtml === html) return;
        this.lastPriceHtml = html;
        this.price.innerHTML = html;
      }

      syncSubmit() {
        const main = this.mainSubmit;
        if (!main || !this.submitButton) return;

        this.submitButton.disabled = main.hasAttribute('disabled');
        this.submitButton.classList.toggle('loading', main.classList.contains('loading'));
        if (main.dataset.ctaState) this.submitButton.dataset.ctaState = main.dataset.ctaState;

        const mainText = main.querySelector('span')?.textContent.trim();
        if (mainText && this.submitText.textContent !== mainText) this.submitText.textContent = mainText;

        // the spinner lives in the main button; mirror it on ours
        const mainSpinner = main.querySelector('.loading__spinner');
        const spinner = this.submitButton.querySelector('.loading__spinner');
        if (mainSpinner && spinner) spinner.classList.toggle('hidden', mainSpinner.classList.contains('hidden'));
      }

      /* ---------- size panel ---------- */

      renderOptions() {
        if (!this.list) return;

        const current = this.mainDropdown?.classList.contains('is-selected')
          ? this.label.textContent.trim()
          : null;

        const markup = this.mainOptions
          .map((option) => {
            const value = option.dataset.variantValue;
            const disabled = option.hasAttribute('disabled');
            const selected = value === current;
            return `<li
              class="pd-sticky-atc__option${selected ? ' is-selected' : ''}"
              role="option"
              tabindex="-1"
              data-value="${value.replace(/"/g, '&quot;')}"
              aria-selected="${selected}"
              ${disabled ? 'aria-disabled="true"' : ''}
            >${option.querySelector('.collapse-variant__name')?.innerHTML.trim() || value}</li>`;
          })
          .join('');

        if (this.list.innerHTML !== markup) this.list.innerHTML = markup;
      }

      togglePanel(force) {
        const open = typeof force === 'boolean' ? force : this.panel.hidden;
        open ? this.openPanel() : this.closePanel();
      }

      openPanel() {
        if (!this.panel) return;
        this.renderOptions();
        this.sizeGuideButton.hidden = !document.getElementById('SizeGuide');
        this.panel.hidden = false;
        this.classList.add('is-open');
        this.toggleButton.setAttribute('aria-expanded', 'true');
        // registered on the next tick so the click that opened the panel
        // (e.g. on the add-to-cart button) does not immediately close it
        setTimeout(() => {
          if (!this.panel.hidden) document.addEventListener('click', this.onDocumentClick);
        });

        const focusTarget = this.list.querySelector('[aria-selected="true"]') || this.list.querySelector('li');
        focusTarget?.focus({ preventScroll: true });
      }

      closePanel(returnFocus = false) {
        if (!this.panel || this.panel.hidden) return;
        this.panel.hidden = true;
        this.classList.remove('is-open');
        this.toggleButton.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocumentClick);
        if (returnFocus) this.toggleButton.focus();
      }

      onDocumentClick(event) {
        if (!this.select.contains(event.target)) this.closePanel();
      }

      onOptionClick(event) {
        const item = event.target.closest('li[data-value]');
        if (!item) return;
        this.choose(item.dataset.value);
      }

      choose(value) {
        const option = this.mainOptions.find((el) => el.dataset.variantValue === value);
        if (!option) return;

        // Select through the main size selector so every existing side effect
        // (variant re-render, back-in-stock form, pre-order message) still runs.
        option.click();
        this.select.classList.remove('has-error');
        this.closePanel(true);
      }

      onKeydown(event) {
        if (!this.panel || this.panel.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closePanel(true);
          return;
        }

        const items = Array.from(this.list.querySelectorAll('li'));
        const index = items.indexOf(document.activeElement);

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          const step = event.key === 'ArrowDown' ? 1 : -1;
          const next = items[(index + step + items.length) % items.length];
          next?.focus();
        } else if ((event.key === 'Enter' || event.key === ' ') && index > -1) {
          event.preventDefault();
          this.choose(items[index].dataset.value);
        }
      }

      openSizeGuide() {
        this.closePanel();
        // main.js opens the drawer from a click on #SizeGuide
        document.getElementById('SizeGuide')?.click();
      }

      /* ---------- add to cart ---------- */

      onSubmit() {
        const main = this.mainSubmit;
        if (!main || main.hasAttribute('disabled')) return;

        const dropdown = this.mainDropdown;
        if (dropdown && !dropdown.classList.contains('is-selected')) {
          // No size yet: flag it on the main selector and here, and open the
          // sticky list so the size can be picked without scrolling back up.
          window.FakeSelector?.showSelectionError(dropdown.closest('.product-variant-selector__item'));
          this.select?.classList.add('has-error');
          this.openPanel();
          return;
        }

        main.click();
      }
    }
  );
}
